import React from 'react'
import { createTheme } from '@material-ui/core/styles'
import { token, px } from './styles/tokens/tokens'
import Icon from './components/ds/Icon'

const ChevronDown = (props) => <Icon name="chevron-down" size={18} {...props} />

const theme = createTheme({
  palette: {
    primary: {
      main: token('rox-dark-green'),
    },

    secondary: {
      main: token('rox-lilac'),
    },
    error: {
      main: token('status-declined'),
    },
    warning: {
      main: token('status-pending'),
    },
    success: {
      main: token('status-approved'),
    },
    info: {
      main: token('status-under-review'),
    },
    text: {
      primary: token('neutral-800'),
      secondary: token('neutral-600'),
    },
    background: {
      default: token('neutral-50'),
      paper: 'var(--bg-surface)',
    },
    divider: token('neutral-300'),
  },
  typography: {
    fontFamily: token('font-sans'),
    fontWeightLight: Number(token('t-weight-light')),
    fontWeightRegular: Number(token('t-weight-regular')),
    fontWeightMedium: Number(token('t-weight-medium')),
    fontWeightBold: Number(token('t-weight-bold')),

  },
  shape: {
    borderRadius: px('radius-md'),
  },
  transitions: {
    easing: {
      easeInOut: token('ease'),
      easeOut: token('ease'),
      easeIn: token('ease'),
      sharp: token('ease'),
    },
    duration: {
      shortest: px('dur-hover'),
      shorter: px('dur-toggle'),
      short: px('dur-toggle'),
      standard: px('dur-pop'),
      complex: px('dur-pop'),
      enteringScreen: px('dur-toggle'),
      leavingScreen: px('dur-toggle'),
    },
  },

  props: {
    MuiSelect: {
      IconComponent: ChevronDown,
    },
  },

  overrides: {
    MuiTab: {
      root: {
        marginRight: '0px!important',

        textTransform: 'none',
        fontSize: token('t-body'),
      },
    },
    MuiTabs: {
      root: {
        marginLeft: '10px',
      },

      indicator: {
        backgroundColor: token('rox-lilac-ink'),
        height: 2,
      },
    },

    MuiButton: {
      root: {
        borderRadius: px('radius-md'),

        textTransform: 'none',
        fontWeight: Number(token('t-weight-medium')),
        boxShadow: 'none',
        transition: `background-color ${token('dur-hover')} ${token('ease')}, transform ${token('dur-hover')} ${token('ease')}`,

        '&:active': { transform: 'translateY(1px)' },
        '&$disabled': {
          backgroundColor: token('bg-sunken'),
          color: token('fg-tertiary'),
        },
      },

      sizeSmall: { minHeight: 32, fontSize: token('t-body-sm') },
      sizeLarge: { minHeight: 48, fontSize: token('t-body') },
      contained: {
        boxShadow: 'none',
        minHeight: 40,
        '&:hover': { boxShadow: 'none' },
        '&:active': { boxShadow: 'none' },
      },
      containedPrimary: {
        backgroundColor: token('rox-dark-green'),
        color: token('fg-on-fill'),
        '&:hover': { backgroundColor: token('btn-primary-hover') },
        '&:active': { backgroundColor: token('rox-dark-green-press') },
      },

      containedSecondary: {
        backgroundColor: token('rox-lilac'),
        color: token('fg-default'),
        '&:hover': { backgroundColor: token('btn-accent-hover') },
        '&:active': { backgroundColor: token('btn-accent-press') },
      },
      outlined: {
        minHeight: 40,
        borderColor: token('border-control'),
        color: token('fg-default'),
        backgroundColor: token('bg-surface'),
        '&:hover': { backgroundColor: token('bg-sunken'), borderColor: token('border-control') },
      },
      text: {
        minHeight: 40,
        color: token('fg-default'),
        '&:hover': { backgroundColor: token('badge-review-bg') },
      },
    },

    MuiAlert: {
      root: {
        borderRadius: px('radius-md'),
        border: `1px solid ${token('border-default')}`,
        backgroundColor: token('bg-surface'),
        color: token('fg-default'),
        fontFamily: token('font-sans'),
        fontSize: token('t-body-sm'),
        alignItems: 'flex-start',
      },
      standardSuccess: {
        backgroundColor: token('badge-approved-bg'),
        borderColor: token('badge-approved-border'),
        color: token('badge-approved-ink'),
        '& $icon': { color: token('badge-approved-ink') },
      },
      standardError: {
        backgroundColor: token('badge-declined-bg'),
        borderColor: token('badge-declined-border'),
        color: token('badge-declined-ink'),
        '& $icon': { color: token('badge-declined-ink') },
      },
      standardWarning: {
        backgroundColor: token('dec-watch-bg'),
        borderColor: token('dec-watch-bd'),
        color: token('dec-watch-ink'),
        '& $icon': { color: token('dec-watch-ink') },
      },
      standardInfo: {
        backgroundColor: token('badge-review-bg'),
        borderColor: token('badge-review-border'),
        color: token('badge-review-ink'),
        '& $icon': { color: token('badge-review-ink') },
      },
      message: { color: 'inherit' },
    },

    MuiInput: {
      underline: {
        '&:before': { borderBottomColor: token('border-control') },
        '&:hover:not($disabled):before': { borderBottomColor: token('neutral-600') },
        '&:after': { borderBottomColor: token('focus-ring') },
      },
    },

    MuiPaper: {
      rounded: { borderRadius: px('radius-lg') },
      elevation0: { boxShadow: 'none' },
      elevation1: { boxShadow: token('sh-1') },
      elevation2: { boxShadow: token('sh-1') },
      elevation3: { boxShadow: token('sh-1') },
      elevation4: { boxShadow: token('sh-2') },
      elevation6: { boxShadow: token('sh-2') },
      elevation8: { boxShadow: token('sh-2') },
      elevation12: { boxShadow: token('sh-3') },
      elevation16: { boxShadow: token('sh-3') },
      elevation24: { boxShadow: token('sh-3') },
    },

    MuiCard: {
      root: {
        borderRadius: px('radius-lg'),
        boxShadow: token('sh-1'),
      },
    },

    MuiPopover: { paper: { boxShadow: token('sh-2'), borderRadius: px('radius-md') } },
    MuiMenu: { paper: { boxShadow: token('sh-2'), borderRadius: px('radius-md') } },

    MuiOutlinedInput: {
      root: {
        borderRadius: px('radius-md'),
        '& $notchedOutline': {
          borderColor: token('border-control'),
          top: 0,
        },
        // The notch only exists to make room for a floating label; with the
        // label above it, the border must run unbroken.
        '& $notchedOutline legend': { display: 'none' },
        '&:hover $notchedOutline': { borderColor: token('neutral-600') },
        '&$focused': {
          boxShadow: `0 0 0 3px ${token('rox-lilac-tint')}`,
        },
        '&$focused $notchedOutline': {
          borderColor: token('focus-ring'),
          borderWidth: 1,
        },
        '&$error $notchedOutline': { borderColor: token('status-declined') },
        '&$disabled': { backgroundColor: token('bg-sunken') },
      },
      // MUI pads 18.5px to clear a floating label. With the label above, that
      // padding only makes the field tall; the DS control is 40px.
      input: {
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 12,
        paddingRight: 12,
        fontSize: token('t-body'),
      },
      inputMarginDense: {
        paddingTop: 8,
        paddingBottom: 8,
      },
    },

    // G1 / 02-1: the DS puts the label ABOVE the control, not floating in a
    // notched border. Doing this in the theme reaches all ~190 MUI TextFields
    // at once; migrating call sites one by one would not.
    // G1 / 02-1: the DS puts the label ABOVE the control, not floating in a
    // notched border. Doing this in the theme reaches all ~190 MUI TextFields
    // at once; migrating call sites one by one would not.
    //
    // `formControl` is the rule that matters: MUI sets it to position:absolute
    // with a translate, which is why an `outlined`-only override left the label
    // sitting on top of the border. All three keys have to move together.
    MuiInputLabel: {
      formControl: {
        position: 'static',
        transform: 'none',
        left: 'auto',
        top: 'auto',
      },
      animated: {
        transition: 'none',
      },
      shrink: {
        transform: 'none',
        transformOrigin: 'top left',
      },
      outlined: {
        position: 'static',
        transform: 'none',
        marginBottom: 6,
        maxWidth: '100%',
        fontSize: token('t-caption'),
        fontWeight: Number(token('t-weight-medium')),
        color: token('fg-default'),
        pointerEvents: 'auto',
        '&$marginDense': { transform: 'none' },
        '&$shrink': { transform: 'none' },
        // A label above the field does not change colour on focus or error;
        // the border and the message line carry those states.
        '&$focused': { color: token('fg-default') },
        '&$error': { color: token('fg-default') },
      },
    },

    MuiFormLabel: {
      root: {
        color: token('fg-secondary'),
        '&$focused': { color: token('focus-ring') },
        '&$error': { color: token('status-declined') },
      },
    },
    MuiFormHelperText: {
      root: {
        color: token('fg-tertiary'),
        '&$error': { color: token('status-declined') },
      },
    },

    MuiTableCell: {
      root: {
        borderBottomColor: token('border-default'),
        fontSize: token('t-body-sm'),
        color: token('fg-default'),
      },
      head: {
        fontSize: token('t-caption'),
        fontWeight: Number(token('t-weight-medium')),
        textTransform: 'uppercase',
        letterSpacing: token('t-tracking-eyebrow'),
        color: token('fg-tertiary'),
      },
    },

    MuiChip: {
      root: {
        backgroundColor: token('tag-bg'),
        color: token('tag-ink'),
        borderRadius: px('radius-pill'),
        fontSize: token('t-body-sm'),
      },
      outlined: {
        backgroundColor: 'transparent',
        borderColor: token('border-control'),
      },
      sizeSmall: { fontSize: token('t-caption') },

      colorPrimary: {
        backgroundColor: token('badge-review-bg'),
        color: token('badge-review-ink'),
      },
      colorSecondary: {
        backgroundColor: token('badge-approved-bg'),
        color: token('badge-approved-ink'),
      },
      deleteIcon: { color: token('fg-tertiary') },
    },

    MuiTooltip: {
      tooltip: {
        backgroundColor: 'rgba(37, 56, 55, 0.9)',
        color: token('rox-off-white'),
        fontSize: token('t-caption'),
        borderRadius: px('radius-sm'),
      },
    },

    MuiDialog: {
      paper: { borderRadius: px('radius-lg') },
    },
    // 02-3: the footer is set off by a tint, which separates the actions from
    // the form without the weight of another rule.
    MuiDialogActions: {
      root: {
        padding: '12px 24px',
        backgroundColor: token('bg-sunken'),
      },
    },
    MuiBackdrop: {
      root: { backgroundColor: 'rgba(37, 56, 55, 0.55)' },
    },

    MuiLink: {
      root: { color: token('link') },
    },

    MuiDivider: {
      root: { backgroundColor: token('border-default') },
    },
  },
});

export default theme
